import { db } from "../lib/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  arrayUnion,
} from "firebase/firestore";
import { Payment, PaymentStatus, PaymentMethod } from "../types/payment";
import { notifyInstructorOfPayment } from "./notificationService";

export const createPayment = async (
  userId: string,
  amount: number,
  description: string,
  method: PaymentMethod,
  courseId?: string,
  subscriptionId?: string
): Promise<string> => {
  const paymentData = {
    userId,
    amount,
    currency: "THB",
    status: "pending" as PaymentStatus,
    method,
    description,
    courseId: courseId || null,
    subscriptionId: subscriptionId || null,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
    metadata: {},
  };

  const docRef = await addDoc(collection(db, "payments"), paymentData);
  return docRef.id;
};

export const updatePaymentStatus = async (
  paymentId: string,
  status: PaymentStatus,
  metadata?: Record<string, any>
): Promise<void> => {
  const paymentRef = doc(db, "payments", paymentId);
  await updateDoc(paymentRef, {
    status,
    updatedAt: Timestamp.now(),
    ...(metadata && { metadata }),
  });
};

export const getPayment = async (paymentId: string): Promise<Payment | null> => {
  const paymentRef = doc(db, "payments", paymentId);
  const paymentSnap = await getDoc(paymentRef);

  if (!paymentSnap.exists()) return null;

  const data = paymentSnap.data();
  return {
    id: paymentSnap.id,
    ...data,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
  } as Payment;
};

export const getUserPayments = async (userId: string): Promise<Payment[]> => {
  const allPayments: Payment[] = [];

  try {
    console.log('[getUserPayments] Fetching payments for userId:', userId);
    
    // 1. Get from 'subscription_payments' collection (paid subscription payments)
    const subscriptionPaymentsQuery = query(
      collection(db, "subscription_payments"),
      where("userId", "==", userId)
    );
    const subscriptionPaymentsSnapshot = await getDocs(subscriptionPaymentsQuery);
    console.log('[getUserPayments] subscription_payments count:', subscriptionPaymentsSnapshot.docs.length);
    
    subscriptionPaymentsSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      console.log('[getUserPayments] subscription_payment:', doc.id, 'status:', data.status, 'userId:', data.userId);
      
      // Filter only approved payments
      if (data.status !== "approved") {
        console.log('[getUserPayments] Skipping non-approved payment:', doc.id);
        return;
      }
      
      // Build description with details
      let description = '';
      const isRenewal = data.isRenewal || false;
      const planName = data.plan || 'Unknown';
      const billingPeriod = data.billingPeriod || 'monthly';
      
      // Main action (ซื้อใหม่ or ต่ออายุ)
      if (isRenewal) {
        description = `ต่ออายุแพ็คเก็จ ${planName}`;
      } else {
        description = `ซื้อแพ็คเก็จ ${planName}`;
      }
      
      // Add billing period
      const periodText = billingPeriod === 'yearly' ? 'รายปี' : 
                        billingPeriod === 'semi_annual' ? 'ราย 6 เดือน' : 'รายเดือน';
      description += ` (${periodText})`;
      
      // Add storage addon if exists
      if (data.storageAddon && data.storageAddon > 0) {
        description += ` + Storage Add-on ${data.storageAddon}GB`;
      }
      
      // Add URL addon if exists
      if (data.urlAddons && Array.isArray(data.urlAddons) && data.urlAddons.length > 0) {
        const urlTypes = data.urlAddons.map((addon: any) => 
          addon.type === 'subdomain' ? 'Subdomain' : 'Path URL'
        ).join(', ');
        description += ` + URL Add-on (${urlTypes})`;
      }
      
      // Use approvedAt as the payment date (when admin approved)
      const paymentDate = data.approvedAt?.toDate() || data.createdAt?.toDate() || new Date();
      
      allPayments.push({
        id: doc.id,
        userId: data.userId,
        amount: data.amount || 0,
        currency: "THB",
        status: "completed",
        method: data.paymentMethod || "bank_transfer",
        description: description,
        courseId: null,
        subscriptionId: data.subscriptionId || null,
        createdAt: paymentDate,
        updatedAt: data.updatedAt?.toDate() || new Date(),
        metadata: {},
      } as Payment);
    });

    // 2. Get from 'addonPayments' collection (Storage and URL add-ons)
    const addonPaymentsQuery = query(
      collection(db, "addonPayments"),
      where("userId", "==", userId)
    );
    const addonPaymentsSnapshot = await getDocs(addonPaymentsQuery);
    console.log('[getUserPayments] addonPayments count:', addonPaymentsSnapshot.docs.length);
    
    addonPaymentsSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      console.log('[getUserPayments] addonPayment:', doc.id, 'status:', data.status, 'userId:', data.userId);
      
      // Filter only approved payments
      if (data.status !== "approved") {
        console.log('[getUserPayments] Skipping non-approved addon payment:', doc.id);
        return;
      }
      
      // Build Add-on description
      let description = '';
      if (data.addonName) {
        description = data.addonName;
      } else if (data.storageGB) {
        description = `ซื้อ Storage Add-on +${data.storageGB}GB`;
      } else if (data.addonType === 'url') {
        description = 'ซื้อ URL Add-on';
      } else {
        description = 'ซื้อ Add-on';
      }
      
      // Use approvedAt as the payment date
      const paymentDate = data.approvedAt?.toDate() || data.createdAt?.toDate() || new Date();
      
      allPayments.push({
        id: doc.id,
        userId: data.userId,
        amount: data.amount || 0,
        currency: "THB",
        status: "completed",
        method: data.paymentMethod || "bank_transfer",
        description: description,
        courseId: null,
        subscriptionId: null,
        createdAt: paymentDate,
        updatedAt: data.updatedAt?.toDate() || new Date(),
        metadata: {},
      } as Payment);
    });

    // 3. Get from 'paymentNotifications' collection (new payment system for subscriptions)
    const paymentNotificationsQuery = query(
      collection(db, "paymentNotifications"),
      where("userId", "==", userId)
    );
    const paymentNotificationsSnapshot = await getDocs(paymentNotificationsQuery);
    console.log('[getUserPayments] paymentNotifications count:', paymentNotificationsSnapshot.docs.length);
    
    paymentNotificationsSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      console.log('[getUserPayments] paymentNotification:', doc.id, 'status:', data.status, 'userId:', data.userId);
      
      // Filter only approved payments
      if (data.status !== "approved") {
        console.log('[getUserPayments] Skipping non-approved payment notification:', doc.id);
        return;
      }
      
      // Build description
      let description = '';
      const planName = data.plan || data.tier || 'Unknown';
      
      if (data.isRenewal) {
        description = `ต่ออายุแพ็คเก็จ ${planName}`;
      } else {
        description = `ซื้อแพ็คเก็จ ${planName}`;
      }
      
      // Use approvedAt as the payment date
      const paymentDate = data.approvedAt?.toDate() || data.createdAt?.toDate() || new Date();
      
      allPayments.push({
        id: doc.id,
        userId: data.userId,
        amount: data.amount || 0,
        currency: "THB",
        status: "completed",
        method: data.paymentMethod || "bank_transfer",
        description: description,
        courseId: null,
        subscriptionId: null,
        createdAt: paymentDate,
        updatedAt: data.updatedAt?.toDate() || new Date(),
        metadata: {},
      } as Payment);
    });

    // 4. Get from 'paymentLogs' collection (free activations, storage addons, etc.)
    const paymentLogsQuery = query(
      collection(db, "paymentLogs"),
      where("userId", "==", userId),
      where("status", "==", "completed")
    );
    const paymentLogsSnapshot = await getDocs(paymentLogsQuery);
    console.log('[getUserPayments] paymentLogs count:', paymentLogsSnapshot.docs.length);
    
    paymentLogsSnapshot.docs.forEach((doc) => {
      const data = doc.data();
      console.log('[getUserPayments] paymentLog:', doc.id, 'type:', data.type, 'userId:', data.userId);
      
      // Build description based on type
      let description = '';
      if (data.type === 'storage_addon') {
        description = `Storage Add-on +${data.storageAddon || 0} GB`;
      } else if (data.type === 'subscription') {
        const tierName = data.tier || 'Unknown';
        description = `เปิดใช้งานแพ็คเก็จ ${tierName}`;
      } else {
        description = data.type || 'Unknown';
      }
      
      // Add discount info if exists
      if (data.discountCode) {
        description += ` (โค้ด: ${data.discountCode})`;
      }
      
      const paymentDate = data.timestamp?.toDate() || new Date();
      
      allPayments.push({
        id: doc.id,
        userId: data.userId,
        amount: data.amount || 0,
        currency: "THB",
        status: "completed",
        method: data.paymentMethod || "free_activation",
        description: description,
        courseId: null,
        subscriptionId: null,
        createdAt: paymentDate,
        updatedAt: paymentDate,
        metadata: {
          type: data.type,
          storageAddon: data.storageAddon,
          discountCode: data.discountCode,
          discountPercent: data.discountPercent
        },
      } as Payment);
    });

  } catch (error) {
    console.error("Error fetching user payments:", error);
  }

  // Sort by createdAt descending (newest first)
  allPayments.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  console.log('[getUserPayments] Total payments found:', allPayments.length);
  console.log('[getUserPayments] Payments:', allPayments);

  return allPayments;
};

export const processCoursePayment = async (
  userId: string,
  courseId: string,
  amount: number,
  method: PaymentMethod,
  autoApprove: boolean = true
): Promise<string> => {
  const paymentId = await createPayment(
    userId,
    amount,
    `ซื้อคอร์ส`,
    method,
    courseId
  );

  if (!autoApprove) {
    return paymentId;
  }

  // Simulate payment processing
  // In production, integrate with actual payment gateway
  await new Promise((resolve) => setTimeout(resolve, 2000));

  // Update payment status to completed
  await updatePaymentStatus(paymentId, "completed");

  // Add course to user's purchased courses
  const userRef = doc(db, "users", userId);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    const userData = userSnap.data();
    const purchasedCourses = userData.purchasedCourses || [];
    
    if (!purchasedCourses.includes(courseId)) {
      await updateDoc(userRef, {
        purchasedCourses: [...purchasedCourses, courseId],
      });
    }
  }

  return paymentId;
};

export const processSubscriptionPayment = async (
  userId: string,
  subscriptionId: string,
  amount: number,
  method: PaymentMethod
): Promise<string> => {
  const paymentId = await createPayment(
    userId,
    amount,
    `ชำระค่า Subscription`,
    method,
    undefined,
    subscriptionId
  );

  // Simulate payment processing
  await new Promise((resolve) => setTimeout(resolve, 2000));

  // Update payment status
  await updatePaymentStatus(paymentId, "completed");

  // Update subscription with payment info
  const subscriptionRef = doc(db, "subscriptions", subscriptionId);
  await updateDoc(subscriptionRef, {
    lastPaymentId: paymentId,
    updatedAt: Timestamp.now(),
  });

  return paymentId;
};

export const createInvoice = async (
  userId: string,
  paymentId: string,
  amount: number,
  description: string
): Promise<string> => {
  const invoiceData = {
    userId,
    paymentId,
    amount,
    currency: "THB",
    description,
    createdAt: Timestamp.now(),
  };

  const docRef = await addDoc(collection(db, "invoices"), invoiceData);
  return docRef.id;
};

export const getUserInvoices = async (userId: string) => {
  const q = query(
    collection(db, "invoices"),
    where("userId", "==", userId),
    orderBy("createdAt", "desc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt.toDate(),
  }));
};

export const createPendingCoursePayment = async (
  userId: string,
  userName: string,
  courseId: string,
  courseTitle: string,
  instructorId: string,
  amount: number,
  method: PaymentMethod,
  paymentProofUrl?: string
): Promise<string> => {
  const paymentId = await createPayment(
    userId,
    amount,
    `ซื้อคอร์ส: ${courseTitle}`,
    method,
    courseId
  );

  if (paymentProofUrl) {
    await updateDoc(doc(db, "payments", paymentId), {
      metadata: { paymentProofUrl },
    });
  }

  const pendingEnrollmentData = {
    userId,
    userName,
    courseId,
    courseTitle,
    instructorId,
    amount,
    paymentId,
    status: "pending",
    paymentProofUrl: paymentProofUrl || null,
    createdAt: Timestamp.now(),
  };

  await addDoc(collection(db, "pendingEnrollments"), pendingEnrollmentData);

  await notifyInstructorOfPayment(
    instructorId,
    userId,
    userName,
    courseId,
    courseTitle,
    amount,
    paymentId
  );

  return paymentId;
};

export const approvePendingPayment = async (
  paymentId: string,
  userId: string,
  courseId: string
): Promise<void> => {
  await updatePaymentStatus(paymentId, "completed");

  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, {
    purchasedCourses: arrayUnion(courseId),
  });

  const q = query(
    collection(db, "pendingEnrollments"),
    where("paymentId", "==", paymentId)
  );
  const snapshot = await getDocs(q);
  
  for (const docSnap of snapshot.docs) {
    await updateDoc(doc(db, "pendingEnrollments", docSnap.id), {
      status: "approved",
    });
  }
};
